import { useLoaderData, useNavigation, useNavigate, useRevalidator } from '@remix-run/react';
import { useState, useEffect } from 'react';
import { atom } from 'nanostores';
import type { UIMessage } from 'ai';
import { toast } from 'react-toastify';
import { workbenchStore } from '~/lib/stores/workbench';
import { getFileActions, getMessages, openDatabase, runProjectIdMigration, setFileActions, setMessages } from './db';
import type { StoreFileAction } from '~/types/actions';
import {
  githubBranchStore,
  githubOwnerStore,
  githubRepoStore,
  supabaseProjectIdStore,
  supabaseProjectNameStore,
  supabaseAnonKeyStore,
  supabaseOrganizationIdStore,
  supabaseOrganizationNameStore,
} from '~/lib/stores/integrations';
import { type ConnectionData, type Connections } from '~/services/interfaceService';
import type { ChatServerLoaderData } from '~/routes/chat.$projectId';
import { v4 as uuidv4 } from 'uuid';

const persistenceEnabled = !import.meta.env.VITE_DISABLE_PERSISTENCE;

console.log('[projectId] openDatabase called from useChatHistory');

export const db = persistenceEnabled ? await openDatabase() : undefined;

export const description = atom<string | undefined>(undefined);
export const initSourceStore = atom<string>('alpha');

/*
 * export const chatId = atom<string | undefined>(undefined); // equivalent to projectId in the new schema
 * export const urlIdStore = atom<string | undefined>(undefined);
 */

export const projectId = atom<string | undefined>(undefined);

// cloudRevert (burst)
// export const cloudChatId = atom<string | undefined>(undefined);
// export const versionId = atom<string | undefined>(undefined);

// Lock to prevent concurrent project creation
// let projectCreationPromise: Promise<void> | null = null;

const getData = async () => {
  const { firstAIArtifact } = workbenchStore;

  if (!description.get()) {
    const repoName = githubRepoStore.get();
    const ownerName = githubOwnerStore.get();
    const branch = githubBranchStore.get();
    const isGitRepo = !!(repoName && ownerName && branch);

    if (isGitRepo) {
      description.set(repoName);
    } else if (firstAIArtifact?.title) {
      description.set(firstAIArtifact.title);
    } else {
      description.set('Untitled Project');
    }
  }

  console.log('[flow] getData projectId: ', projectId.get());

  if (!projectId.get()) {
    const pId = uuidv4();

    projectId.set(pId);
    navigateChat(pId);

    // cloudRevert (burst)
    // // If a project is already being created, wait for it
    // if (projectCreationPromise) {
    //   console.log('[flow] Project creation already in progress, waiting...');
    //   await projectCreationPromise;
    //   return;
    // }

    // // Create a new promise and store it
    // projectCreationPromise = (async () => {
    //   try {
    //     console.log('[flow] Starting project creation...');

    //     const { framework, language, packageManager } = techStackSelectionStore.get();
    //     const repoName = githubRepoStore.get();
    //     const ownerName = githubOwnerStore.get();
    //     const branch = githubBranchStore.get();
    //     const isGitRepo = !!(repoName && ownerName && branch);

    //     console.log('[getData] description from !projectId: ', description.get());

    //     await createCloudProject(
    //       framework,
    //       language,
    //       packageManager,
    //       initSourceStore.get(),
    //       isGitRepo,
    //       description.get() || '',
    //     );
    //     console.log('[getData] Project creation completed successfully');
    //   } finally {
    //     // Clear the promise once done
    //     projectCreationPromise = null;
    //   }
    // })();

    // await projectCreationPromise;
  }
};

export function useChatHistory() {
  const navigation = useNavigation();
  const navigate = useNavigate();

  const loaderData = useLoaderData<ChatServerLoaderData>();
  const { projectId: mixedProjectId, token, pendingMessage: loaderPendingMessage } = loaderData;

  // cloudRevert (burst)
  // const { token, projectData, hasCache: cacheHit, isCacheValid, eventsToDelete } = loaderData;
  // const {
  //   projectId: mixedProjectId,
  //   cloudChatId: mixedCloudChatId,
  //   currentVersion,
  //   projectName,
  //   messages: loaderMessages,
  //   fileActions: loaderFileActions,
  //   type: projectType,
  // } = projectData ?? {};

  console.log(
    '[useChatHistory] data from loader, token, projectId, isCacheValid, projectData and eventsToDelete:',
    token,
    projectId,
  );

  const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
  const [initialFileActions, setInitialFileActions] = useState<StoreFileAction[]>([]);
  const [ready, setReady] = useState<boolean>(false);
  const [showCloudProjectErrorModal, setShowCloudProjectErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const revalidate = useRevalidator();

  useEffect(() => {
    console.log('[projectId] location of useChatHistory: ', window.location.href);

    if (db && token && !window.location.href.includes('chat/')) {
      console.log('[projectId] Token available in useChatHistory, triggering deferred migration if needed');
      runProjectIdMigration(db, token).then(() => {
        // cloudRevert
        // revalidate.revalidate();
      });
    }
  }, [token]);

  useEffect(() => {
    // Wait for navigation to complete
    if (navigation.state === 'loading') {
      console.log('[useChatHistory] Navigation still loading, waiting...');
      return;
    }

    // Check for pending message from loader data - this indicates a new project from home page
    // We now use server-side storage instead of sessionStorage
    const hasPendingMessage = !!loaderPendingMessage;

    if (!db) {
      // If there's a pending message, set projectId before marking ready
      if (hasPendingMessage && mixedProjectId) {
        console.log('[useChatHistory] New project with pending message (no db), setting projectId');
        projectId.set(mixedProjectId);
      }

      setReady(true);

      if (persistenceEnabled) {
        toast.error(`Chat persistence is unavailable`);
      }

      return;
    }

    /*
     * console.log(
     *   '[useChatHistory] mixedId:',
     *   mixedId,
     *   'mixedProjectId:',
     *   mixedProjectId,
     *   'mixedCloudChatId:',
     *   mixedCloudChatId,
     *   'currentVersion:',
     *   currentVersion,
     * );
     */

    if (!mixedProjectId) {
      setReady(true);
      return;
    }

    // If there's a pending message, this is a new project - set projectId and let ChatPage handle the rest
    if (hasPendingMessage) {
      console.log('[useChatHistory] New project detected with pending message from loader, setting projectId early');
      projectId.set(mixedProjectId);
      setReady(true);
      return;
    }

    const loadChatData = async () => {
      try {
        console.log('[useChatHistory] loading chat data for existing project');

        // cloudRevert (burst)
        // ensureEventStatusConnection(mixedProjectId as string, mixedCloudChatId, token!);
        // update the project's lastOpenedOn timestamp in db
        // const lastOpenedOn = new Date().toISOString()
        // updateLastOpenedOn(mixedProjectId, lastOpenedOn);

        /*
         * Note: Not refreshing sidebar here - this runs every time a chat loads
         * and would cause unnecessary API calls just for a timestamp update
         */

        // cloudRevert (burst)
        // Load and restore failed events from IndexedDB for current project only
        // if (mixedProjectId) {
        //   try {
        //     const failedEvents = await getFailedEventsFromDB(db, mixedProjectId);

        //     if (failedEvents.length > 0) {
        //       console.log(
        //         '[useChatHistory] Restoring failed events from IndexedDB for project:',
        //         mixedProjectId,
        //         failedEvents.length,
        //       );

        //       // Restore each failed event to the event status store
        //       for (const event of failedEvents) {
        //         const error: EventErrorDetails = event.error
        //           ? event.error
        //           : {
        //               message: 'ENQUEUE_ERROR',
        //               code: 'ENQUEUE_ERROR',
        //               retryable: true,
        //               errorType: 'ENQUEUE_ERROR',
        //               metadata: {
        //                 eventId: event.eventId,
        //                 projectId: event.projectId,
        //                 cloudChatId: event.cloudChatId,
        //               },
        //             };

        //         if (error) {
        //           setEventStatus(event.eventId, {
        //             status: 'FAILED',
        //             eventState: error.errorType,
        //             projectId: event.projectId,
        //             chatId: event.cloudChatId,
        //             userId: event.userId,
        //             timestamp: new Date().toISOString(),
        //             error,
        //           });
        //           console.log('[useChatHistory] Restored failed event:', event.eventId);
        //         }
        //       }
        //     }
        //   } catch (error) {
        //     console.warn('[useChatHistory] Failed to load failed events from IndexedDB:', error);
        //   }
        // }

        // loading data from IDB
        // if (cacheHit && isCacheValid) {
        console.log('[useChatHistory] Loading from IDB (server skipped query)');

        try {
          const cached = await getMessages(db, mixedProjectId);

          console.log('[useChatHistory] cached:', cached);

          console.log('[useChatHistoryy] cached type:', cached?.type);

          if (cached && cached.messages && cached.messages.length > 0) {
            // cloudRevert (burst)
            // cached.messages = cached.messages.map((msg) => ({
            //   ...msg,
            //   content: msg.content,
            // }));
            // cloudChatId.set(cached.cloudChatId);

            description.set(cached.description);
            projectId.set(cached.projectId);
            setInitialMessages(cached.messages);
            setInitialFileActions(cached.fileActions);
          } else {
            // No cached data - redirect to home (new projects are handled earlier in the useEffect)
            console.log('[chat.$projectId.$id] no cached data found, navigating to home');
            navigate(`/home`, { replace: true });
            return;
          }
        } catch (error) {
          toast.error((error as Error).message || 'Failed to load chat data');
        }

        // }

        // cloudRevert (burst)
        //  else {
        //   // fetching data from cloud
        //   if (!mixedCloudChatId || !currentVersion) {
        //     setReady(true);
        //     throw new Error('Mixed cloud chat ID or current version not found');
        //   }

        //   console.log('[useChatHistory] cloudserver response:', loaderFileActions, loaderMessages);

        //   projectId.set(mixedProjectId);
        //   description.set(projectName ?? undefined);
        //   cloudChatId.set(mixedCloudChatId);
        //   versionId.set(currentVersion);

        //   // Cache to IDB
        //   try {
        //     const messages = (loaderMessages ?? []).map((msg) => ({
        //       ...msg,
        //       createdAt: msg.createdAt ? new Date(msg.createdAt) : undefined,
        //     })) as UIMessage[];
        //     const fileActions = (loaderFileActions ?? []) as StoreFileAction[];

        //     await setMessages(
        //       db,
        //       mixedProjectId,
        //       messages,
        //       projectData?.techStack?.framework ?? '',
        //       projectData?.techStack?.language ?? '',
        //       projectData?.techStack?.packageManager ?? '',
        //       projectData?.isGitRepo ?? false,
        //       projectData?.initSource ?? undefined,
        //       projectData?.projectName ?? undefined,
        //       projectData?.connections ?? undefined,
        //       token,
        //       mixedCloudChatId,
        //       projectData?.cacheTime ?? undefined,
        //       undefined,
        //       undefined,
        //       true,
        //     );
        //     await setFileActions(db, mixedProjectId, mixedCloudChatId, fileActions, true);

        //     if (projectData?.netlifyCreds) {
        //       updateNetlifyUrl(db, mixedProjectId, projectData.netlifyCreds);
        //     }

        //     setInitialMessages(messages);
        //     setInitialFileActions(fileActions);
        //     console.log('[useChatHistory] fileActions set in IDB');
        //   } catch (error) {
        //     console.warn('[hit] IDB cache write failed:', error);
        //   }
        // }

        // cloudRevert (burst)
        // Delete completed events in the background
        // if (eventsToDelete && eventsToDelete.length > 0) {
        //   console.log('[useChatHistory] Deleting completed events');
        //   deleteEvents(db, eventsToDelete).catch((error) => {
        //     console.warn('[DualiteEvents] Failed to delete completed events:', error);
        //   });
        // }

        setReady(true);
      } catch (error) {
        toast.error((error as Error).message || 'Failed to load chat data');
        throw new Error('Failed to load chat data');

        // navigate('/', { replace: true });
      }
    };

    loadChatData();
  }, [navigation.state, mixedProjectId]);

  const handleCloseErrorModal = () => {
    setShowCloudProjectErrorModal(false);
    window.location.href = '/';
  };

  return {
    ready: !mixedProjectId || ready,
    initialMessages,
    initialFileActions,
    showCloudProjectErrorModal,
    errorMessage,
    handleCloseErrorModal,
    storeMessageHistory: async (
      messages: UIMessage[],
      framework: string,
      language: string,
      packageManager: string,
      token: string,
      isGitRepo: boolean,
      initSource: string,
      connections?: Connections,
    ) => {
      console.log('[supa] storeMessageHistory called from useChatHistory: ', connections);

      if (!db || messages.length === 0) {
        return;
      }

      await getData();

      console.log('[flow] storeMessageHistory called from useChatHistory, projectId');

      await setMessages(
        db as IDBDatabase,
        projectId.get() as string,
        messages,
        framework,
        language,
        packageManager,
        (isGitRepo = isGitRepo || false),
        initSource,
        description.get(),
        connections,
        token,

        // cloudChatId.get() as string,
      );
      console.log('[refresh] storeMessageHistory setMessages called:');
    },
    storeFileActions: async (fileActions: StoreFileAction[]) => {
      if (!db || fileActions.length === 0) {
        return;
      }

      console.log('[flow] set file actions called from storeFileActions inside return function in usechathistory');

      await getData();
      await setFileActions(
        db as IDBDatabase,
        projectId.get() as string,
        fileActions,
        false,

        // cloudChatId.get() as string,
      );
    },
    getFileActions: async () => {
      if (!db) {
        return [];
      }

      return await getFileActions(db as IDBDatabase, projectId.get() as string);
    },
  };
}

function navigateChat(id: string) {
  // url -> /chat/<projectId>
  const url = new URL(window.location.href);
  url.pathname = `/chat/${id}`;
  window.history.replaceState({}, '', url);
}

export async function storeFileActions(fileActions: StoreFileAction[], changeSource: 'manual' | 'ai' = 'ai') {
  console.log('[storeFileActions] fileActions length: ', fileActions.length, ' db: ', db);

  if (!db || fileActions.length === 0) {
    return;
  }

  console.log('[storeFileActions] fileActions: ', fileActions);

  await getData();

  console.log('[flow] set file actions called from storeFileActions');

  await setFileActions(
    db as IDBDatabase,
    projectId.get() as string,
    fileActions,
    false,
    changeSource,

    // cloudChatId.get() as string,
  );
}

export function setDescriptionFromExamplePrompt(title: string) {
  description.set(title);
}

/*
 * update projectId in indexedDB
 * const updateProjectIdInIDB = async (pId: string) => {
 *   if (db) {
 *     await setProjectId(db as IDBDatabase, pId);
 *   }
 * };
 */

// after project is created, add github connection, if present
const updateGithubConnection = async () => {
  const repoName = githubRepoStore.get();
  const ownerName = githubOwnerStore.get();
  const branch = githubBranchStore.get();

  const isGitRepo = repoName && ownerName && branch;
  const pId = projectId.get();

  if (isGitRepo && pId) {
    const connectionData: ConnectionData = {
      connectionType: 'github',
      connectionData: {
        repoName,
        ownerName,
        branch,
      },
    };

    console.log('[projects] projectId from github connection: ', pId);

    // cloudRevert (burst)
    // await updateProjectConnection(pId, connectionData);
  }
};

const updateSupabaseConnection = async () => {
  const supabaseProjectId = supabaseProjectIdStore.get();
  const supabaseProjectName = supabaseProjectNameStore.get();
  const supabaseAnonKey = supabaseAnonKeyStore.get();
  const supabaseOrganizationId = supabaseOrganizationIdStore.get();
  const supabaseOrganizationName = supabaseOrganizationNameStore.get();

  const pId = projectId.get();

  if (
    pId &&
    supabaseProjectId &&
    supabaseProjectName &&
    supabaseAnonKey &&
    supabaseOrganizationId &&
    supabaseOrganizationName
  ) {
    const connectionData: ConnectionData = {
      connectionType: 'supabase',
      connectionData: {
        anonKey: supabaseAnonKey,
        organizationId: supabaseOrganizationId,
        organizationName: supabaseOrganizationName,
        projectId: supabaseProjectId,
        projectName: supabaseProjectName,
      },
    };

    // cloudRevert (burst)
    // await updateProjectConnection(pId, connectionData);
  }
};

// cloudRevert (burst)
// const createCloudProject = async (
//   framework: string,
//   language: string,
//   packageManager: string,
//   initSource: string,
//   isGitRepo: boolean,
//   description: string,
// ) => {
//   console.log('[flow] Conditions met for project upload, preparing data...');

//   const [fwork, cssLib] = framework.split('_');
//   const techStack = {
//     framework: fwork,
//     cssLib,
//     language,
//     packageManager,
//   };
//   const source = initSource == 'alpha' ? 'alpha' : 'github';

//   const valuesArray = [
//     {
//       currentVersion: null,
//       projectName: description || '',
//       techStack,
//       isGitRepo,
//       initSource: source,
//       type: 'local' as const, // all new projects are now created as local again projects
//       urlId: null,
//       idbChatId: null,
//       createdAt: new Date(),
//     },
//   ];

//   try {
//     const response = await fetch('/api/projects', {
//       method: 'POST',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ projectData: valuesArray, type: 'create' }),
//     });

//     const result = (await response.json()) as {
//       ok: boolean;
//       message: { projectId: string; cloudChatId: string };
//       error: string;
//     };

//     console.log('[flow] projectId from createCloudProject: ', result.message?.projectId);

//     if (result.ok) {
//       const { projectId: pId } = result.message;
//       projectId.set(pId);

//       // cloudRevert (burst)
//       // cloudChatId.set(cId);

//       navigateChat(pId);

//       updateGithubConnection();
//       updateSupabaseConnection();

//       // update the sidebar with the new project
//       const newProject: CloudChatHistoryItem = {
//         ...valuesArray[0],
//         projectId: pId,
//         currentVersion: null,
//         timestamp: valuesArray[0].createdAt.toISOString(),
//         projectRules: null,
//         cacheTime: null,

//         // cloudRevert (burst)
//         // cloudChatId: cId,
//       };

//       await addProjectToStore(newProject);
//       console.log('[updateProjectInStore] New project added to sidebar');
//     }
//   } catch (error) {
//     console.error('[projects] Error creating cloud project:', error);
//     Sentry.captureException(`Error from createCloudProject in useChatHistory.ts: ${error}`, {
//       extra: {
//         framework,
//         language,
//         packageManager,
//         initSource,
//       },
//     });

//     /*
//      * setErrorMessage('Failed to create project. Please try again.');
//      * setShowCloudProjectErrorModal(true);
//      */
//     toast.error('Failed to create project');
//   }
// };
